import React, { useState } from "react";
import { MapPin, Search, Loader2, BadgeCheck } from "lucide-react";
import { valuationLookup } from "../api";
import { useToast } from "../hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

// Enter a postcode → get the full numbered list of addresses at that postcode
// (same method as the Instant Valuation tool — PropertyData EPC records). Pick one to fill address + postcode.
const PostcodeAddressPicker = ({ postcode, address, onPick, compact = false, testid = "postcode-address" }) => {
  const { toast } = useToast();
  const [pc, setPc] = useState(postcode || "");
  const [addresses, setAddresses] = useState([]);
  const [busy, setBusy] = useState(false);

  const find = async () => {
    const q = pc.trim();
    if (q.length < 5) { toast({ title: "Enter a full postcode", description: "e.g. BA2 5DZ" }); return; }
    setBusy(true);
    try {
      const d = await valuationLookup(q);
      setAddresses(d.addresses || []);
      onPick?.({ postcode: (d.postcode || q).toUpperCase() });
      if (!d.addresses?.length) toast({ title: "No addresses found", description: "You can still type the address manually." });
      else toast({ title: `${d.addresses.length} addresses found`, description: "Pick yours from the list." });
    } catch { toast({ title: "Lookup failed", description: "Try a full postcode." }); }
    finally { setBusy(false); }
  };

  const pick = (addr) => {
    const match = addresses.find((a) => a.address === addr);
    onPick?.({ address: addr, postcode: (pc || postcode || "").toUpperCase(), sqft: match?.sqft || null });
  };

  const h = compact ? "h-[44px]" : "h-[54px]";
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b8935f]" />
          <input
            value={pc}
            onChange={(e) => setPc(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); find(); } }}
            placeholder="Enter postcode (e.g. BA2 5DZ)"
            data-testid={`${testid}-postcode`}
            className={`w-full ${h} pl-9 pr-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition`}
          />
        </div>
        <button type="button" onClick={find} disabled={busy} data-testid={`${testid}-find`}
          className={`shrink-0 px-5 ${h} rounded-lg bg-[#1c1c1e] text-white text-[14px] font-500 hover:bg-black transition-colors disabled:opacity-60 inline-flex items-center gap-2`}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Find address
        </button>
      </div>
      {addresses.length > 0 && (
        <Select value={address || ""} onValueChange={pick}>
          <SelectTrigger className={`${h} border-[#e2d8c8]`} data-testid={`${testid}-select`}>
            <SelectValue placeholder={`Select the address (${addresses.length} found)`} />
          </SelectTrigger>
          <SelectContent className="max-h-[260px]">
            {addresses.map((a) => (
              <SelectItem key={a.address} value={a.address}>{a.address}{a.sqft ? ` · ${Number(a.sqft).toLocaleString()} sq ft` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {address && (
        <p className="text-[12px] text-[#6a6a6a] pl-1" data-testid={`${testid}-selected`}>
          <BadgeCheck size={12} className="inline text-[#b8935f]" /> {address}
        </p>
      )}
    </div>
  );
};

export default PostcodeAddressPicker;
